import React, { useState, useEffect } from 'react';
import Sidebar from '../../components/navigation/Sidebar';

import NotificationIndicator from '../../components/navigation/NotificationIndicator';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import InvoiceFilters from './components/InvoiceFilters';
import InvoiceTable from './components/InvoiceTable';
import InvoiceDetailPanel from './components/InvoiceDetailPanel';
import CreateInvoiceModal from './components/CreateInvoiceModal';
import RecordPaymentModal from './components/RecordPaymentModal';
import FinancialSummary from './components/FinancialSummary';

const BillingInvoice = () => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('language') || 'en';
  });

  const [direction, setDirection] = useState(() => {
    return localStorage.getItem('direction') || 'ltr';
  });

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentInvoice, setPaymentInvoice] = useState(null);

  const [filters, setFilters] = useState({
    status: 'all',
    billingType: 'all',
    client: 'all',
    dateFrom: '',
    dateTo: '',
    search: ''
  });

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language') || 'en';
    const savedDirection = localStorage.getItem('direction') || 'ltr';
    setLanguage(savedLanguage);
    setDirection(savedDirection);
    document.documentElement?.setAttribute('dir', savedDirection);
  }, []);

  const mockInvoices = [
    {
      id: 1,
      invoiceNumber: "INV-2024-001",
      clientName: language === 'fr' ? "Fatima El Amrani" : language === 'ar' ? "فاطمة العمراني" : "Fatima El Amrani",
      billingType: "flat_fee",
      amount: 15000,
      paidAmount: 15000,
      status: "paid",
      issueDate: "2024-01-15",
      dueDate: "2024-02-15",
      subtotal: 12500,
      tax: 2500,
      lineItems: [
        {
          description: language === 'fr' ? "Consultation juridique initiale" : language === 'ar' ? "استشارة قانونية أولية" : "Initial Legal Consultation",
          quantity: 1,
          rate: 5000,
          amount: 5000
        },
        {
          description: language === 'fr' ? "Rédaction de contrat commercial" : language === 'ar' ? "صياغة عقد تجاري" : "Commercial Contract Drafting",
          quantity: 1,
          rate: 7500,
          amount: 7500
        }
      ],
      paymentHistory: [
        {
          date: "2024-01-20",
          amount: 15000,
          method: language === 'fr' ? "Virement bancaire" : language === 'ar' ? "تحويل بنكي" : "Bank Transfer",
          reference: "TRX-2024-001"
        }
      ]
    },
    {
      id: 2,
      invoiceNumber: "INV-2024-002",
      clientName: language === 'fr' ? "Mohammed Benali" : language === 'ar' ? "محمد بنعلي" : "Mohammed Benali",
      billingType: "hourly",
      amount: 24000,
      paidAmount: 12000,
      status: "partial",
      issueDate: "2024-01-18",
      dueDate: "2024-02-18",
      subtotal: 20000,
      tax: 4000,
      lineItems: [
        {
          description: language === 'fr' ? "Services juridiques - Janvier 2024" : language === 'ar' ? "خدمات قانونية - يناير 2024" : "Legal Services - January 2024",
          quantity: 20,
          rate: 1000,
          amount: 20000
        }
      ],
      paymentHistory: [
        {
          date: "2024-01-25",
          amount: 12000,
          method: language === 'fr' ? "Chèque" : language === 'ar' ? "شيك" : "Check",
          reference: "CHQ-2024-045"
        }
      ]
    },
    {
      id: 3,
      invoiceNumber: "INV-2024-003",
      clientName: language === 'fr' ? "Société TechMaroc SARL" : language === 'ar' ? "شركة تك مغرب ذ.م.م" : "TechMaroc SARL Company",
      billingType: "per_hearing",
      amount: 36000,
      paidAmount: 0,
      status: "pending",
      issueDate: "2024-01-20",
      dueDate: "2024-02-20",
      subtotal: 30000,
      tax: 6000,
      lineItems: [
        {
          description: language === 'fr' ? "Représentation audience commerciale" : language === 'ar' ? "تمثيل في جلسة تجارية" : "Commercial Hearing Representation",
          quantity: 3,
          rate: 10000,
          amount: 30000
        }
      ],
      paymentHistory: []
    },
    {
      id: 4,
      invoiceNumber: "INV-2024-004",
      clientName: language === 'fr' ? "Amina Tazi" : language === 'ar' ? "أمينة التازي" : "Amina Tazi",
      billingType: "flat_fee",
      amount: 18000,
      paidAmount: 0,
      status: "overdue",
      issueDate: "2023-12-15",
      dueDate: "2024-01-15",
      subtotal: 15000,
      tax: 3000,
      lineItems: [
        {
          description: language === 'fr' ? "Conseil juridique familial" : language === 'ar' ? "استشارة قانونية عائلية" : "Family Law Consultation",
          quantity: 1,
          rate: 8000,
          amount: 8000
        },
        {
          description: language === 'fr' ? "Préparation documents légaux" : language === 'ar' ? "إعداد المستندات القانونية" : "Legal Document Preparation",
          quantity: 1,
          rate: 7000,
          amount: 7000
        }
      ],
      paymentHistory: []
    },
    {
      id: 5,
      invoiceNumber: "INV-2024-005",
      clientName: language === 'fr' ? "Fatima El Amrani" : language === 'ar' ? "فاطمة العمراني" : "Fatima El Amrani",
      billingType: "hourly",
      amount: 30000,
      paidAmount: 0,
      status: "pending",
      issueDate: "2024-01-22",
      dueDate: "2024-02-22",
      subtotal: 25000,
      tax: 5000,
      lineItems: [
        {
          description: language === 'fr' ? "Recherche juridique approfondie" : language === 'ar' ? "بحث قانوني متعمق" : "Comprehensive Legal Research",
          quantity: 25,
          rate: 1000,
          amount: 25000
        }
      ],
      paymentHistory: []
    }
  ];

  const financialSummary = {
    totalRevenue: 123000,
    collected: 27000,
    pending: 66000,
    overdue: 18000
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
  };

  const handleResetFilters = () => {
    setFilters({
      status: 'all',
      billingType: 'all',
      client: 'all',
      dateFrom: '',
      dateTo: '',
      search: ''
    });
  };

  const handleViewDetails = (invoice) => {
    setSelectedInvoice(invoice);
  };

  const handleDownloadPDF = (invoice) => {
    console.log('Downloading PDF for invoice:', invoice?.invoiceNumber);
    alert(`${language === 'fr' ? 'Téléchargement de la facture' : language === 'ar' ? 'تحميل الفاتورة' : 'Downloading invoice'} ${invoice?.invoiceNumber}`);
  };

  const handleRecordPayment = (invoice) => {
    setPaymentInvoice(invoice);
    setShowPaymentModal(true);
  };

  const handleCreateInvoice = (formData) => {
    console.log('Creating invoice:', formData);
    setShowCreateModal(false);
    alert(language === 'fr' ? 'Facture créée avec succès!' : language === 'ar' ? 'تم إنشاء الفاتورة بنجاح!' : 'Invoice created successfully!');
  };

  const handleSubmitPayment = (paymentData) => {
    console.log('Recording payment:', paymentData);
    setShowPaymentModal(false);
    setPaymentInvoice(null);
    alert(language === 'fr' ? 'Paiement enregistré avec succès!' : language === 'ar' ? 'تم تسجيل الدفع بنجاح!' : 'Payment recorded successfully!');
  };

  const filteredInvoices = mockInvoices?.filter(invoice => {
    if (filters?.status !== 'all' && invoice?.status !== filters?.status) return false;
    if (filters?.billingType !== 'all' && invoice?.billingType !== filters?.billingType) return false;
    if (filters?.client !== 'all') {
      const clientMap = {
        'client_1': language === 'fr' ? 'Fatima El Amrani' : language === 'ar' ? 'فاطمة العمراني' : 'Fatima El Amrani',
        'client_2': language === 'fr' ? 'Mohammed Benali' : language === 'ar' ? 'محمد بنعلي' : 'Mohammed Benali',
        'client_3': language === 'fr' ? 'Société TechMaroc SARL' : language === 'ar' ? 'شركة تك مغرب ذ.م.م' : 'TechMaroc SARL Company',
        'client_4': language === 'fr' ? 'Amina Tazi' : language === 'ar' ? 'أمينة التازي' : 'Amina Tazi'
      };
      if (invoice?.clientName !== clientMap?.[filters?.client]) return false;
    }
    if (filters?.search && !invoice?.invoiceNumber?.toLowerCase()?.includes(filters?.search?.toLowerCase())) return false;
    if (filters?.dateFrom && new Date(invoice.issueDate) < new Date(filters.dateFrom)) return false;
    if (filters?.dateTo && new Date(invoice.issueDate) > new Date(filters.dateTo)) return false;
    return true;
  });

  const pageTitle = {
    en: 'Billing & Invoices',
    fr: 'Facturation',
    ar: 'الفواتير'
  };

  const createInvoiceLabel = {
    en: 'Create Invoice',
    fr: 'Créer une facture',
    ar: 'إنشاء فاتورة'
  };

  const exportLabel = {
    en: 'Export Report',
    fr: 'Exporter le rapport',
    ar: 'تصدير التقرير'
  };

  return (
    <div className="min-h-screen bg-background" style={{ direction }}>
      <Sidebar
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
      />
      <div
        className={`transition-all duration-250 pt-16 lg:pt-0 ${
          sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-60'
        }`}
      >
        <header className="sticky top-0 z-40 bg-card border-b border-border shadow-elevation-sm">
          <div className="px-4 md:px-6 lg:px-8 py-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex-1">
                <h1 className="text-lg md:text-xl lg:text-2xl xl:text-3xl font-semibold text-foreground mb-1">
                  {language === 'fr' ? 'Facturation et Factures' : language === 'ar' ? 'الفواتير والمحاسبة' : 'Billing & Invoices'}
                </h1>
                <p className="text-xs md:text-sm text-muted-foreground">
                  {language === 'fr' ? 'Gérez vos factures et suivez vos paiements' : language === 'ar' ? 'إدارة الفواتير وتتبع المدفوعات' : 'Manage invoices and track payments'}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <NotificationIndicator language={language} />
                <Button
                  onClick={() => setShowCreateModal(true)}
                  className="flex items-center gap-2 whitespace-nowrap"
                >
                  <Icon name="Plus" size={18} />
                  <span className="hidden sm:inline">
                    {language === 'fr' ? 'Nouvelle Facture' : language === 'ar' ? 'فاتورة جديدة' : 'New Invoice'}
                  </span>
                  <span className="sm:hidden">
                    {language === 'fr' ? 'Nouveau' : language === 'ar' ? 'جديد' : 'New'}
                  </span>
                </Button>
              </div>
            </div>
          </div>
        </header>

        <main className="px-4 md:px-6 lg:px-8 py-6">
          <FinancialSummary language={language} summary={financialSummary} />

          <InvoiceFilters
            language={language}
            filters={filters}
            onFilterChange={handleFilterChange}
            onReset={handleResetFilters}
          />

          <InvoiceTable
            language={language}
            invoices={filteredInvoices}
            onViewDetails={handleViewDetails}
            onDownloadPDF={handleDownloadPDF}
            onRecordPayment={handleRecordPayment}
          />

          {filteredInvoices?.length === 0 && (
            <div className="bg-card border border-border rounded-lg p-12 text-center">
              <Icon name="FileText" size={64} className="text-muted-foreground mx-auto mb-4" />
              <p className="text-lg text-muted-foreground">
                {language === 'fr' ? 'Aucune facture trouvée' : language === 'ar' ? 'لم يتم العثور على فواتير' : 'No invoices found'}
              </p>
            </div>
          )}
        </main>
      </div>
      {selectedInvoice && (
        <InvoiceDetailPanel
          language={language}
          invoice={selectedInvoice}
          onClose={() => setSelectedInvoice(null)}
        />
      )}
      {showCreateModal && (
        <CreateInvoiceModal
          language={language}
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateInvoice}
        />
      )}
      {showPaymentModal && paymentInvoice && (
        <RecordPaymentModal
          language={language}
          invoice={paymentInvoice}
          onClose={() => {
            setShowPaymentModal(false);
            setPaymentInvoice(null);
          }}
          onSubmit={handleSubmitPayment}
        />
      )}
    </div>
  );
};

export default BillingInvoice;